import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAccessibility } from '../contexts/AccessibilityContext';
import { dbHelpers } from '../lib/supabase';
import { trackBookmarkAdd, trackBookmarkRemove } from '../utils/analytics';
// Removed unused import: mockSessions
// Removed unused import: getMasterProgramme
import { fetchSessions } from '../services/supabaseService';
import { useConferenceYear } from '../contexts/ConferenceYearContext';
import AacButton from '../components/aac/AacButton';
import '../styles/SessionCard.css';
import { ChevronUp } from 'lucide-react';
import { useListNavigation } from '../hooks/useListNavigation';

const Programme = () => {
  const { announce, focusFirstHeading, showShortcutBadges } = useAccessibility();
  const { activeYear } = useConferenceYear();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  // Restore filters left over from before the user navigated to a session's
  // detail page, so returning "back" shows the same filtered view.
  const [filters, setFilters] = useState(() => {
    try {
      const saved = sessionStorage.getItem('programme:filters');
      return saved ? JSON.parse(saved) : { day: '', track: '', search: '' };
    } catch (e) {
      return { day: '', track: '', search: '' };
    }
  });
  const [bookmarkedSessions, setBookmarkedSessions] = useState(new Set());
  const [showBackToTop, setShowBackToTop] = useState(false);
  const hasRestoredScroll = useRef(false);

  useEffect(() => {
    sessionStorage.setItem('programme:filters', JSON.stringify(filters));
  }, [filters]);

  // One-time scroll restore to wherever the user was when they left for a
  // session's detail page, consumed so a fresh visit to Programme doesn't
  // inherit a stale scroll position.
  useEffect(() => {
    if (hasRestoredScroll.current || loading || filteredSessions.length === 0) return;
    hasRestoredScroll.current = true;
    const savedScrollY = sessionStorage.getItem('programme:scrollY');
    if (savedScrollY) {
      sessionStorage.removeItem('programme:scrollY');
      requestAnimationFrame(() => window.scrollTo(0, parseInt(savedScrollY, 10)));
    }
  }, [loading, filteredSessions]);

  const saveProgrammeReturnState = () => {
    sessionStorage.setItem('programme:scrollY', String(window.scrollY));
  };
  
  // CRITICAL FIX: Ensure bookmarkedSessions is always a Set using useMemo for stability
  const safeBookmarkedSessions = useMemo(() => {
    const safe = bookmarkedSessions instanceof Set ? bookmarkedSessions : new Set();
    // Memoized safe bookmarked sessions to prevent re-creation on every render
    return safe;
  }, [bookmarkedSessions]);
  
  // Ensure bookmarkedSessions is always a Set for safe operations
  
  // Move formatDate function to top to avoid hoisting issues - wrapped in useCallback to prevent infinite loops
  const formatDate = useCallback((dateInput) => {
    // Handle null/undefined inputs
    if (!dateInput) {
      return 'Invalid Date';
    }
    
    
    // If it's just a day name (Monday, Tuesday, etc.), return it as-is
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    if (typeof dateInput === 'string' && dayNames.includes(dateInput)) {
      return dateInput;
    }
    
    let date;
    // If input is already a Date object, use it directly
    if (dateInput instanceof Date) {
      date = dateInput;
    } else if (typeof dateInput === 'string') {
      // Parse the datetime string as local time to avoid timezone conversion
      if (dateInput.includes('T') && !dateInput.includes('Z') && !dateInput.includes('+')) {
        // Parse as local time by manually extracting components
        const [datePart, timePart] = dateInput.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hour, minute] = timePart ? timePart.split(':').map(Number) : [0, 0];
        date = new Date(year, month - 1, day, hour, minute);
      } else {
        date = new Date(dateInput);
      }
    } else {
      return 'Invalid Date';
    }
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    // Get individual components for custom formatting with ordinal
    const weekday = date.toLocaleDateString('en-GB', { weekday: 'long' });
    const day = date.getDate();
    const month = date.toLocaleDateString('en-GB', { month: 'long' });
    
    // Add ordinal suffix to day
    const getOrdinalSuffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    const formatted = `${weekday} ${day}${getOrdinalSuffix(day)} ${month}`;
    
    return formatted;
  }, []);

  // Back to top functionality
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowBackToTop(scrollTop > 300);
    };

    // Test scroll event immediately
    window.addEventListener('scroll', handleScroll);
    
    // Test initial scroll position
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    announce('Scrolled to top of page');
  }, [announce]);

  const loadSessions = useCallback(async () => {
    try {
      
      let sessionsData;
      try {
        sessionsData = await fetchSessions(activeYear);
        announce(`Conference programme loaded from database (${sessionsData?.length || 0} sessions)`);
      } catch (supabaseError) {
        console.error('❌ Failed to load sessions from Supabase:', supabaseError);
        sessionsData = [];
        announce('Conference programme could not be loaded');
      }

      // Sort sessions with special sessions at top, then by session number
      const sortedSessions = (sessionsData || []).sort((a, b) => {

        // Define special sessions that should appear at the top
        const isSpecialSessionA = a.title?.includes("Newbie's Icebreaker Meeting") || 
                                 (a.title?.includes("Best Research Evidence") && a.title?.includes("Identity and Connections"));
        const isSpecialSessionB = b.title?.includes("Newbie's Icebreaker Meeting") || 
                                 (b.title?.includes("Best Research Evidence") && b.title?.includes("Identity and Connections"));
        
        // Special sessions priority: Newbie's first, then Keynote
        if (isSpecialSessionA && !isSpecialSessionB) {
          return -1;
        }
        if (!isSpecialSessionA && isSpecialSessionB) {
          return 1;
        }
        
        // If both are special sessions, Newbie's comes before Keynote
        if (isSpecialSessionA && isSpecialSessionB) {
          const isNewbieA = a.title?.includes("Newbie's Icebreaker Meeting");
          const isNewbieB = b.title?.includes("Newbie's Icebreaker Meeting");
          if (isNewbieA && !isNewbieB) return -1;
          if (!isNewbieA && isNewbieB) return 1;
        }
        
        // Sort primarily by date & start time (ascending). Sessions with no
        // date/time are pushed to the end rather than sorted arbitrarily.
        const getSessionDateTime = (session) => {
          const datePart = session.date || session.sessionDate;
          const timePart = session.start_time;

          if (datePart && timePart) {
            const parsed = new Date(`${datePart}T${timePart}`);
            if (!isNaN(parsed)) {
              return parsed;
            }
          }

          if (session.formatted_time && session.formatted_time !== 'TBA') {
            const startTimeStr = session.formatted_time.split(' - ')[0];
            const parsed = new Date(`${datePart || '2000-01-01'}T${startTimeStr}`);
            if (!isNaN(parsed)) {
              return parsed;
            }
          }

          return null;
        };

        const dateTimeA = getSessionDateTime(a);
        const dateTimeB = getSessionDateTime(b);

        if (dateTimeA && dateTimeB) {
          const diff = dateTimeA - dateTimeB;
          if (diff !== 0) {
            return diff;
          }
        } else if (dateTimeA && !dateTimeB) {
          return -1;
        } else if (!dateTimeA && dateTimeB) {
          return 1;
        }

        // Same date/time (or both missing): fall back to predefined track
        // order so the list stays stable rather than jumping around.
        const predefinedTrackOrder = [
          'M1', 'Keynote', '1.1', '1.2', '1.3', '1.4', '1.5', '1.6', '1.7', '1.8',
          'M2', 'M3', '2.1', '2.2', '2.3', '2.4', '2.5', '2.6', '2.7', '2.8',
          '3.1', '3.2', '3.3', '3.4', '3.5', '3.6', '3.7', '3.8', 'M4',
          '4.1', '4.2', '4.3', '4.4', '4.5', '4.6', '4.7', '4.8', '4.9',
          '5.1', '5.2', '5.3', '5.4', '5.5', '5.6', '5.7', '5.8',
          'P1', 'P2', 'P3', 'P4', 'Plenary',
          '6.1', '6.2', '6.3', '6.4', '6.5', '6.6', '6.7', '6.8',
          '7.1', '7.2', '7.3', '7.4', '7.5', '7.6', '7.7', '7.8', '7.9',
          '8.1', '8.2', '8.3', '8.4', '8.5', '8.6', '8.7', '8.8',
          '9.1', '9.2', '9.3', '9.4', '9.5', '9.6', '9.7', '9.8',
          'P5', 'P6', 'P7', 'P8', 'P9', 'P10', 'P11', 'P12', 'P13', 'P14',
          'P15', 'P16', 'P17', 'P18', 'P19', 'P20', 'P21', 'P22', 'P23', 'P24',
          'P25', 'P26', 'P27', 'P28', 'P29', 'P30', 'P31', 'P32', 'P33', 'P34'
        ];

        const getTrackSortValue = (track) => {
          if (!track) {
            return 9999; // Default for missing tracks - place at end
          }

          // Clean track value - remove tabs and extra content
          let cleanTrack = track;
          if (typeof track === 'string') {
            if (track.includes('\t')) {
              cleanTrack = track.split('\t')[0];
            }
            if (track.includes('Speaker') && track.includes('Main Hall')) {
              cleanTrack = 'Keynote';
            }
          }

          // Find the track in the predefined order
          const position = predefinedTrackOrder.indexOf(cleanTrack);

          if (position !== -1) {
            return position;
          }

          // Handle tracks not in predefined list - place at end
          return 9999;
        };

        const positionA = getTrackSortValue(a.track);
        const positionB = getTrackSortValue(b.track);

        return positionA - positionB;
      });
      
      // Poster-format sessions have their own dedicated Posters tab and are
      // excluded from the main Programme list.
      const nonPosterSessions = sortedSessions.filter((s) => s.format !== 'Poster');

      setSessions(nonPosterSessions);
    } catch (error) {
      setSessions([]);
      announce('Conference programme could not be loaded');
    } finally {
      setLoading(false);
    }
  }, [announce, activeYear]);
  const loadUserBookmarks = useCallback(async () => {
    try {
      const { data: bookmarkIds } = await dbHelpers.getUserBookmarks();

      // Update local state
      const bookmarkSet = new Set(Array.isArray(bookmarkIds) ? bookmarkIds : []);
      setBookmarkedSessions(bookmarkSet);
    } catch (error) {
      console.error('🔖 BOOKMARK LOADING ERROR: Error loading bookmarks:', error);
      setBookmarkedSessions(new Set());
    }
  }, []);

  // Load sessions on component mount
  useEffect(() => {
    // Focusing the heading scrolls it into view, which would immediately
    // undo the scroll-position restore below when returning from a
    // session's detail page — so skip it whenever a return position is
    // waiting to be restored.
    const isReturningToProgramme = !!sessionStorage.getItem('programme:scrollY');

    const initializeData = async () => {
      try {
        await loadSessions();
        await loadUserBookmarks();
        if (!isReturningToProgramme) {
          focusFirstHeading();
        }
      } catch (error) {
        console.error('❌ Error during initialization:', error);
      }
    };
    
    initializeData();
  }, [loadSessions, loadUserBookmarks, focusFirstHeading]); // Include dependencies

  // Filter sessions when filters or sessions change
  useEffect(() => {
    let filtered = [...sessions];
    
    // Filter by day - only apply filter if day is not 'all' or empty
    if (filters.day && filters.day !== 'all' && filters.day !== '') {
      filtered = filtered.filter(session => {
        // Use the day field directly if available, otherwise check if formatted date starts with filter
        const sessionDay = session.day || formatDate(session.date);
        const matches = sessionDay === filters.day || (sessionDay && sessionDay.startsWith && sessionDay.startsWith(filters.day)) || (sessionDay && sessionDay.includes && sessionDay.includes(filters.day));
        return matches;
      });
    } else {
    }
    
    // Filter by track - only apply filter if track is not 'all' or empty
    if (filters.track && filters.track !== 'all' && filters.track !== '') {
      filtered = filtered.filter(session => {
        const sessionTrack = session.track || session.session_number;
        const matches = sessionTrack && sessionTrack.toString().startsWith(filters.track);
        return matches;
      });
    } else {
    }
    
    // Filter by search term
    if (filters.search && filters.search.trim() !== '') {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(session => {
        return (
          session.title?.toLowerCase().includes(searchTerm) ||
          session.speaker?.toLowerCase().includes(searchTerm) ||
          session.description?.toLowerCase().includes(searchTerm) ||
          session.category?.toLowerCase().includes(searchTerm)
        );
      });
    } else {
    }
    
    setFilteredSessions(filtered);
    
    // Note: Removed announce call to prevent infinite loop
    // The announce function creates new references on each render
  }, [sessions, filters, formatDate]);
  const toggleBookmark = async (sessionId) => {
    const isCurrentlyBookmarked = safeBookmarkedSessions.has(sessionId);
    
    try {
      let result;
      if (isCurrentlyBookmarked) {
        result = await dbHelpers.removeBookmark(sessionId);
      } else {
        result = await dbHelpers.addBookmark(sessionId);
      }
      
      
      if (result.error) {
        console.error('🔖 BOOKMARK DEBUG: Error from dbHelpers:', result.error);
        return;
      }
      
      if (isCurrentlyBookmarked) {
        trackBookmarkRemove(sessionId, 'session');
      } else {
        trackBookmarkAdd(sessionId, 'session');
      }
      
      setBookmarkedSessions(prev => {
        const newBookmarks = new Set(prev);
        if (isCurrentlyBookmarked) {
          newBookmarks.delete(sessionId);
        } else {
          newBookmarks.add(sessionId);
        }
        return newBookmarks;
      });
      
    } catch (error) {
      console.error('❌ BOOKMARK DEBUG: Error toggling bookmark:', error);
    }
  };
  const { currentItem: currentNavSession } = useListNavigation(filteredSessions, {
    onOpen: (session) => {
      saveProgrammeReturnState();
      navigate(`/session/${session.session_number || session.id}`);
    },
    onToggleAgenda: (session) => toggleBookmark(session.id),
    getLabel: (session) => session.title,
    getId: (session) => `${session.id}-${session.track}-${session.session_number || 'default'}`,
  });
  const currentNavSessionKey = currentNavSession
    ? `${currentNavSession.id}-${currentNavSession.track}-${currentNavSession.session_number || 'default'}`
    : null;

  // Scroll/focus the current list-nav session only when it actually changes
  // (via N/P), not on every unrelated re-render. An inline ref callback on
  // the article would fire on every render (it's a fresh function each
  // time), which meant any state change elsewhere on the page — including
  // the T "back to top" shortcut's own accessibility announcement — caused
  // this card to immediately scrollIntoView/focus itself again, making it
  // look like the page was "stuck" and unable to scroll away from it.
  const sessionRefs = useRef(new Map());
  useEffect(() => {
    if (!currentNavSessionKey) return;
    const el = sessionRefs.current.get(currentNavSessionKey);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    el.focus({ preventScroll: true });
  }, [currentNavSessionKey]);
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };
  const clearFilters = () => {
    setFilters({ day: 'all', track: 'all', search: '' });
    announce('Filters cleared');
  };
  // Get unique days and tracks for filter options
  const uniqueTracks = [...new Set(sessions.map(session => session.track).filter(Boolean))].sort();

  if (loading) {
    return (
      <div className="container" style={{ padding: 'var(--spacing-xl) var(--spacing-md)' }}>
        <div className="loading-content" role="status" aria-live="polite">
          <h1>Loading Conference Programme...</h1>
          <div className="loading-spinner" aria-hidden="true" />
        </div>
      </div>
    );
  }
  return (
    <div className="programme-page" data-testid="programme-page">
      <div className="container">
        <header className="page-header">
          <h1>Conference Programme</h1>
          <p className="page-description">
            Browse all conference sessions, bookmark your favorites, and build your personal agenda.
          </p>
          <a
            href="/CM2026_Abstract_Booklet.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline"
          >
            📄 Download Abstract Booklet (PDF)
          </a>
        </header>
        {/* Filters */}
        <section className="filters-section" aria-labelledby="filters-title" data-testid="filter-sort-options">
          <h2 id="filters-title" className="sr-only">Filter Sessions</h2>
          <div className="filters-grid">
            {/* Search */}
            <div className="filter-group">
              <label htmlFor="search-input" className="filter-label">
                Search Sessions
              </label>
              <input
                id="search-input"
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search by title, description, or speaker..."
                className="filter-input"
                aria-describedby="search-help"
              />
              <div id="search-help" className="sr-only">
                Search through session titles, descriptions, and speaker names
              </div>
            </div>
            
            {/* Quick Day Filter Buttons */}
            <div className="filter-group">
              <label className="filter-label">Quick Day Filter</label>
              <div className="quick-day-filters">
                <button
                  className={`day-filter-btn ${filters.day === 'Monday' ? 'active' : ''}`}
                  onClick={() => {
                    handleFilterChange('day', filters.day === 'Monday' ? '' : 'Monday');
                  }}
                >
                  Monday
                </button>
                <button
                  className={`day-filter-btn ${filters.day === 'Tuesday' ? 'active' : ''}`}
                  onClick={() => {
                    handleFilterChange('day', filters.day === 'Tuesday' ? '' : 'Tuesday');
                  }}
                >
                  Tuesday
                </button>
              </div>
            </div>
            

            {/* Track Filter */}
            <div className="filter-group">
              <label htmlFor="track-filter" className="filter-label">
                Session Track
              </label>
              <select
                id="track-filter"
                value={filters.track}
                onChange={(e) => handleFilterChange('track', e.target.value)}
                className="filter-select"
              >
                <option value="all">All Tracks</option>
                {uniqueTracks.map(track => (
                  <option key={track} value={track}>
                    {track}
                  </option>
                ))}
              </select>
            </div>
            {/* Clear Filters */}
            <div className="filter-group">
              <AacButton
                onClick={clearFilters}
                variant="secondary"
                shortcut={1}
                disabled={filters.day === 'all' && filters.track === 'all' && !filters.search}
              >
                Clear Filters
              </AacButton>
            </div>
          </div>
          {/* Results Count */}
          <div className="results-info" role="status" aria-live="polite">
            Showing {filteredSessions.length} of {sessions.length} sessions
          </div>
        </section>
        {/* Sessions List */}
        <section className="sessions-section" aria-labelledby="sessions-title">
          <h2 id="sessions-title" className="sr-only">Session List</h2>

          {filteredSessions.length === 0 ? (
            <div className="no-results" role="status" data-testid="empty-sessions">
              <h3>No sessions found</h3>
              <p>Try adjusting your filters or search terms.</p>
              <button onClick={clearFilters} className="btn btn-primary">
                Clear All Filters
              </button>
            </div>
          ) : (
            <div className="sessions-list" data-testid="sessions-list">

              {filteredSessions.map((session) => {
                
                // Parse and format location to show building name with room number.
                // `location` is usually just a bare room code ("LG19") with the
                // building living in its own `building` field, not combined —
                // so a naive `session.location` alone silently drops the
                // building name for most sessions. Combine both explicitly,
                // still handling the rarer case where location already comes
                // as "Building, Room".
                const formatLocation = (location, building) => {
                  if ((!location || location === 'TBA') && !building) {
                    return 'Location TBA';
                  }

                  if (location) {
                    const parts = location.split(',').map(part => part.trim());
                    if (parts.length >= 2) {
                      return `${parts[0]} - ${parts[1]}`;
                    }
                  }

                  const roomPart = location && location !== 'TBA' ? location : null;
                  return [building, roomPart].filter(Boolean).join(' - ') || 'Location TBA';
                };

                const formattedLocation = formatLocation(session.location, session.building);
                
                
                const sessionKey = `${session.id}-${session.track}-${session.session_number || 'default'}`;
                const isCurrentNavSession = currentNavSessionKey === sessionKey;

                return (
                  <article
                    key={sessionKey}
                    ref={(el) => {
                      if (el) {
                        sessionRefs.current.set(sessionKey, el);
                      } else {
                        sessionRefs.current.delete(sessionKey);
                      }
                    }}
                    tabIndex={-1}
                    className={`session-card ${session.status === 'cancelled' ? 'cancelled' : ''} ${isCurrentNavSession ? 'list-nav-current' : ''}`}
                  >
                    {/* Session Header */}
                    <div className="session-header">
                      <h2 className="session-number">
                        {session.track || session.session_number || 'TBA'}
                      </h2>
                    </div>
                    
                    {/* Session Title */}
                    <div className="session-title-section">
                      <h3 className={`session-title ${session.status === 'cancelled' ? 'cancelled' : ''}`}>
                        <Link to={`/session/${session.session_number || session.id}`} onClick={saveProgrammeReturnState}>
                          {session.title}
                          {session.status === 'cancelled' && <span className="cancelled-indicator"> (CANCELLED)</span>}
                        </Link>
                      </h3>
                      {session.status === 'cancelled' && (
                        <span className="status-badge status-cancelled">Cancelled</span>
                      )}
                    </div>
                    
                    {/* Session Details */}
                    <div className="session-details">
                      <div className="details-grid">
                        {/* Time & Date */}
                        <div className="detail-item">
                          <span className="detail-label">Time & Date</span>
                          <span className="detail-value">
                            {(() => {
                              return `${session.time || 'TBA'} • ${formatDate(session.date) || 'TBA'}`;
                            })()}
                          </span>
                        </div>

                        {/* Location */}
                        <div className="detail-item">
                          <span className="detail-label">Location</span>
                          <span className="detail-value">
                            {formattedLocation}
                          </span>
                        </div>

                        {/* Speaker(s) */}
                        <div className="detail-item">
                          <span className="detail-label">Speaker(s)</span>
                          <span className="detail-value">
                            {session.speaker || 'TBA'}
                          </span>
                        </div>

                        {/* Track */}
                        <div className="detail-item">
                          <span className="detail-label">Track</span>
                          <span className="detail-value">
                            {session.track || 'Track TBA'}
                          </span>
                        </div>

                        {/* Category */}
                        <div className="detail-item">
                          <span className="detail-label">Category</span>
                          <span className="detail-value">
                            {session.category || 'Category TBA'}
                          </span>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="session-actions">
                        <Link
                          to={`/session/${session.session_number || session.id}`}
                          className="btn btn-primary"
                          onClick={saveProgrammeReturnState}
                          aria-keyshortcuts={isCurrentNavSession ? 'O' : undefined}
                          title={isCurrentNavSession ? 'View details (press O)' : undefined}
                        >
                          View Details
                          {isCurrentNavSession && showShortcutBadges && (
                            <span className="btn-shortcut-badge" aria-hidden="true">O</span>
                          )}
                        </Link>

                        <button
                          onClick={() => {
                            toggleBookmark(session.id);
                          }}
                          className={`btn ${safeBookmarkedSessions.has(session.id) ? 'btn-success' : 'btn-outline'}`}
                          aria-keyshortcuts={isCurrentNavSession ? 'A' : undefined}
                          title={isCurrentNavSession ? 'Add to agenda (press A)' : undefined}
                        >
                          {safeBookmarkedSessions.has(session.id) ? 'In Agenda' : 'Add to Agenda'}
                          {isCurrentNavSession && showShortcutBadges && (
                            <span className="btn-shortcut-badge" aria-hidden="true">A</span>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Session Description */}
                    <div className="session-description-section">
                      <h4 className="description-title">Description</h4>
                      <p className="session-description">
                        {(() => {
                          // Special handling for keynote sessions - prioritize description over abstract
                          let content;
                          if (session.track === 'Keynote' || session.title?.toLowerCase().includes('keynote')) {
                            // For keynotes: prioritize description > abstract
                            content = session.description || session.abstract;
                          } else {
                            // For regular sessions: prioritize abstract > description
                            content = session.abstract || session.description;
                          }

                          if (content && content.length > 200) {
                            return `${content.substring(0, 200)}...`;
                          }
                          return content || 'Description will be updated soon.';
                        })()}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>
      
      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="back-to-top-btn"
          aria-label="Scroll to top of page"
          aria-keyshortcuts="T"
          title="Back to top (press T)"
        >
          <ChevronUp size={24} aria-hidden="true" />
          <span className="sr-only">Back to top (keyboard shortcut: T)</span>
        </button>
      )}
    </div>
  );
};
const ProgrammeComponent = Programme;
export default ProgrammeComponent;
